import "./styles/firstrun.css";   // the unlock cards' rules: kept with the feature, not in the shared entry sheet
import { BalanceData, ownedTowers } from "@highmarch/domain";
import { audio } from "../audio/audio";
import { icon } from "./iconsKit";
import { motionOk, ribbonHtml } from "./kit";
import { portraitUrl } from "./portraits";
import { petPortraitImg } from "./pets/petArt";

/**
 * Unlock cards (ENGAGEMENT 2026-09-23 §B2, feel report ★1): after COLLECT on a win that opened something — Gear and
 * Chests with the first clear, a new tower with each clear that brings one — short cards play one after another, long
 * enough to read (a title and a line), over whatever is on screen; a tap skips to the next. The chest is banked (and the cards parked, main.ts)
 * when OPEN is tapped; COLLECT plays them (chestReveal.ts), and the hub plays any the player never collected.
 */

/** Gear and Chests open with the first cleared level (progressive disclosure: the hub's tabs are locked until then). */
export const HUB_UNLOCK_LEVEL = 1;
/** How long one card holds before the next: a title and a short line at phone reading speed (DESIGN §4.4; 1 s was too fast). */
export const UNLOCK_CARD_MS = 2200;
/** A parked set older than this belongs to an earlier battle (the player went on without collecting) and is dropped. */
const MAX_AGE_MS = 120_000;

export interface UnlockCard {
  /** The ribbon: "GEAR UNLOCKED", "NEXT: FIRE". */
  readonly title: string;
  /** One line on the plate under it. */
  readonly line: string;
  /** Kit icon for a feature card… */
  readonly icon?: string;
  /** …or the tower whose portrait it shows… */
  readonly tower?: string;
  /** …or the pet (docs/PETS.md), its portrait over the icon (which stays if the portrait has not shipped). */
  readonly pet?: string;
}

/** What a clear moving the highest cleared level from `before` to `after` unlocks, in the order the cards play. */
export function unlockCards(B: BalanceData, before: number, after: number): UnlockCard[] {
  if (after <= before) return [];
  const cards: UnlockCard[] = [];
  if (before < HUB_UNLOCK_LEVEL && after >= HUB_UNLOCK_LEVEL) {
    cards.push({ title: "GEAR UNLOCKED", line: "Arm your towers and fortress with gear", icon: "bag" });
    cards.push({ title: "CHESTS UNLOCKED", line: "Every battle pays a chest: better play, better chest", icon: "chest" });
  }
  const had = ownedTowers(B, before), has = ownedTowers(B, after);
  B.towers.filter((t) => has[t.index] && !had[t.index]).sort((a, b) => a.unlockLevel - b.unlockLevel)
    .forEach((t) => cards.push({ title: `NEXT: ${t.name.toUpperCase()}`, line: `Joins your ranks at level ${t.unlockLevel}`, tower: t.id }));
  // The features that open with a later clear (FINISH_PLAN WP9 O4). Each card plays on the one clear that crosses its
  // level, so once per save; Captain Bram's hub line then points at the thing itself.
  for (const f of featureUnlocks(B)) if (before < f.afterLevel && after >= f.afterLevel) cards.push(f.card);
  return cards;
}

/** A feature's unlock card and the cleared level that opens it (from balance; a feature switched off has none). */
export function featureUnlocks(B: BalanceData): { readonly afterLevel: number; readonly card: UnlockCard }[] {
  const out: { afterLevel: number; card: UnlockCard }[] = [];
  const tribute = B.commerce.tribute;
  if (tribute.capSeconds > 0) out.push({ afterLevel: tribute.unlockAfterLevel, card: { title: "TRIBUTE UNLOCKED", line: "The fortress gathers gold while you're away", icon: "coin" } });
  out.push({ afterLevel: B.events.unlockAfterLevel, card: { title: "EVENTS UNLOCKED", line: "Heroic replays and more, in the Events tab", icon: "flame" } });
  if (B.events.trial.modifiers.length > 0) out.push({ afterLevel: B.events.trial.unlockAfterLevel, card: { title: "WEEKLY TRIAL UNLOCKED", line: "A cleared level with a fresh twist each week", icon: "bolt" } });
  return out.sort((a, b) => a.afterLevel - b.afterLevel);
}

let parked: { cards: UnlockCard[]; at: number } | null = null;

/** Holds the cards of the battle just won until COLLECT (or the hub) plays them. */
export function parkUnlockCards(cards: readonly UnlockCard[]): void { parked = cards.length > 0 ? { cards: [...cards], at: Date.now() } : null; }

/** Plays the parked cards over `host`, once; `onDone` runs when they are gone (at once if there were none). */
export function playParkedUnlockCards(host: HTMLElement, onDone: () => void = () => undefined): boolean {
  const p = parked;
  parked = null;
  if (!p || Date.now() - p.at > MAX_AGE_MS) { onDone(); return false; }
  showUnlockCards(host, p.cards, onDone);
  return true;
}

/** True while cards are waiting (the hub's guide holds until they have played). */
export function unlockCardsPending(): boolean { return parked !== null; }

/** Card overlays on screen now. */
let playing = 0;
/** True while cards are on screen (the guide's "unlockCards" busy reason). */
export function unlockCardsPlaying(): boolean { return playing > 0; }

function cardHtml(c: UnlockCard): string {
  const art = c.tower ? portraitUrl(c.tower) : null;
  const pet = c.pet ? petPortraitImg(c.pet, null, `class="upet" onload="this.parentElement.classList.add('petart')"`) : "";
  const well = art ? `<img decoding="sync" src="${art}" alt="">` : `${icon(c.icon ?? "star", 44)}${pet}`;
  return `<div class="ucard">${ribbonHtml(c.title, "", "utitle")}<div class="uplate panel"><div class="uwell${art ? " art" : ""}">${well}</div><p>${c.line}</p></div></div>`;
}

/** The overlay: one card at a time, `UNLOCK_CARD_MS` each or until tapped; dots show how many are left. */
export function showUnlockCards(host: HTMLElement, cards: readonly UnlockCard[], onDone: () => void): void {
  const el = document.createElement("div");
  el.className = `unlockcards${motionOk() ? "" : " still"}`;
  host.append(el);
  playing++;
  let i = -1;
  let timer = 0;
  const next = () => {
    window.clearTimeout(timer);
    i++;
    if (i >= cards.length) { el.remove(); playing--; onDone(); return; }
    const dots = cards.length > 1 ? `<div class="udots">${cards.map((_, k) => `<i class="${k === i ? "on" : ""}"></i>`).join("")}</div>` : "";
    el.innerHTML = `${cardHtml(cards[i])}${dots}<small class="uhint">tap to skip</small>`;
    audio.play("towerUpgrade");
    timer = window.setTimeout(next, UNLOCK_CARD_MS);
  };
  el.onclick = (e) => { e.stopPropagation(); next(); };
  next();
}
